import React, { useEffect, useMemo, useState, useCallback } from "react"
import classNames from "classnames"
import Image from "components/Image"
import PhotoModal from "components/PhotoModal"
import { Tooltip } from "react-tooltip"
import storyblokImageLoader, { centerCropImageLoader } from "utilities/storyblokImageLoader"
import Loading from "/public/assets/outline-logo.svg"
import useWindowSize from "utilities/useWindowSize"

/**
 * @typedef {Object} Photo
 * @property {'small' | 'medium' | 'large'} size - The size of the photo.
 * @property {string} src - The URL of the photo.
 * @property {string} blurDataURL - The URL of the blurred placeholder image.
 * @property {string} uuid - The unique identifier for the photo.
 * @property {string} alt - The alt text for the image.
 * @property {React.Element | string} tooltipContent - The content for the tooltip.
 */

/**
 * A single photo component.
 *
 * @param {{ photo: Photo, fallbackImage: string }} props - The props for the SinglePhoto component.
 */
const SinglePhoto = React.memo(({ photo, fallbackImage, setModal, setClickedUUID }) => {
  const size = photo.size === "triple" ? 298 : photo.size === "double" ? 193 : 89

  return (
    <>
      <div
        id={`photo-${photo.uuid}`}
        className={classNames(
          {
            "col-span-3 row-span-3": photo.size === "triple",
            "col-span-2 row-span-2": photo.size === "double",
            "col-span-1 row-span-1": photo.size === "single",
          },
          "cursor-pointer single-team-photo bg-blue-dark rounded-2xl overflow-hidden hover:scale-105 hover:border-green-kelly hover:border-2 transition-transform relative",
        )}
        onClick={() => {
          setModal(true)
          setClickedUUID(photo.uuid)
        }}
      >
        <Image
          placeholder={photo.blurDataURL ? "blur" : "empty"}
          blurDataURL={photo.blurDataURL}
          src={photo.src || fallbackImage}
          alt={photo.alt}
          sizes={`${size}px`}
          loader={photo.src ? centerCropImageLoader : storyblokImageLoader}
          className="w-full h-full object-cover scale-105 aspect-square"
          width={size}
          height={size}
        />
      </div>
      <Tooltip
        className="z-10 tooltip"
        classNameArrow="tooltip-arrow"
        anchorSelect={`#photo-${photo.uuid}`}
        place="right"
        opacity={1}
      >
        {photo.tooltipContent}
      </Tooltip>
    </>
  )
})

SinglePhoto.displayName = "SinglePhoto"

/**
 * Picks large photos and adjusts their sizes.
 *
 * @param {Photo[]} results - The array of photo objects.
 * @param {boolean} mixSizes - Whether to mix sizes or not.
 * @returns {Photo[]} The array of photo objects with adjusted sizes.
 */
const pickLargePhotos = (results, mixSizes) => {
  if (!mixSizes) {
    return results?.map((photo) => ({ ...photo, size: "single" }))
  }

  const total = results?.length
  const tripleCount = Math.floor(total * 0.04)
  const doubleCount = Math.floor(total * 0.12)

  return results?.map((photo, index) => {
    let size = "single"
    if (index < tripleCount) size = "triple"
    else if (index < tripleCount + doubleCount) size = "double"
    return { ...photo, size }
  })
}

/**
 * Shuffles an array.
 *
 * @param {Array} array - The array to shuffle.
 * @returns {Array} The shuffled array.
 */
const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

/**
 * The PhotoGrid component.
 *
 * @param {{ photos: Photo[], loading: boolean, fallbackImage: string, mixSizes: boolean }} props - The props for the PhotoGrid component.
 * @returns {JSX.Element} The rendered PhotoGrid component.
 */
const PhotoGrid = ({ photos, loading, fallbackImage, mixSizes = true }) => {
  const [showAll, setShowAll] = useState(false)
  const { width } = useWindowSize()
  const [modal, setModal] = useState(false)
  const [modalPhoto, setModalPhoto] = useState({})
  const [clickedUUID, setClickedUUID] = useState(null)
  const initialCols = useMemo(() => (width >= 1024 ? 12 : width >= 640 ? 8 : 4), [width])
  const initialRows = useMemo(() => (width >= 1024 ? 6 : width >= 640 ? 9 : 18), [width])
  const initialCount = useMemo(() => initialCols * initialRows, [initialCols, initialRows])

  const [initialGrid, setInitialGrid] = useState([])
  const [remainingGrid, setRemainingGrid] = useState([])

  const mixedPhotos = useMemo(() => (photos ? pickLargePhotos(photos, mixSizes) : []), [photos, mixSizes])
  const shuffledPhotos = useMemo(() => shuffleArray([...mixedPhotos]), [mixedPhotos])

  useEffect(() => {
    setModalPhoto(photos?.find((photo) => photo.uuid === clickedUUID))
  }, [clickedUUID, modalPhoto, photos])

  const isValidPlacement = useCallback((grid, row, col, size, columns) => {
    if (row + size > grid.length || col + size > columns) return false
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (grid[row + r][col + c]) return false
      }
    }
    return true
  }, [])

  const findNextPlacement = useCallback(
    (grid, size, columns) => {
      for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < columns; col++) {
          if (isValidPlacement(grid, row, col, size, columns)) {
            return { row, col }
          }
        }
      }
      return null
    },
    [isValidPlacement],
  )

  const isAdjacent = useCallback((row1, col1, lastPlacement, placementSize) => {
    const { row: row2, col: col2 } = lastPlacement
    const sizeDiff = placementSize

    return (
      (Math.abs(row1 - row2) < sizeDiff && col1 === col2) ||
      (Math.abs(col1 - col2) < sizeDiff && row1 === row2) ||
      (Math.abs(row1 - row2) < sizeDiff && Math.abs(col1 - col2) < sizeDiff)
    )
  }, [])

  const placePhotos = useCallback(
    (photos, columns, rows = null) => {
      const grid = Array.from({ length: rows || Math.ceil(photos.length / columns) + 301 }, () =>
        Array(columns).fill(null),
      )

      let lastPlacedTriple = null
      let lastPlacedDouble = null

      photos.forEach((photo) => {
        const sizeValue = photo.size === "triple" ? 3 : photo.size === "double" ? 2 : 1
        const placement = findNextPlacement(grid, sizeValue, columns)

        if (placement) {
          const { row, col } = placement

          if (
            (photo.size === "triple" && lastPlacedTriple && isAdjacent(row, col, lastPlacedTriple, 3)) ||
            (photo.size === "double" && lastPlacedDouble && isAdjacent(row, col, lastPlacedDouble, 2))
          ) {
            return
          }

          for (let r = 0; r < sizeValue; r++) {
            for (let c = 0; c < sizeValue; c++) {
              grid[row + r][col + c] = photo
            }
          }

          if (photo.size === "triple") {
            lastPlacedTriple = { row, col }
          } else if (photo.size === "double") {
            lastPlacedDouble = { row, col }
          }
        }
      })

      const placed = grid.flat().reduce((acc, photo) => {
        if (photo && !acc.some((m) => m.uuid === photo.uuid)) {
          acc.push(photo)
        }
        return acc
      }, [])

      const unplaced = photos.filter((photo) => !placed.some((m) => m.uuid === photo.uuid))
      return { placed, unplaced }
    },
    [findNextPlacement, isAdjacent],
  )

  useEffect(() => {
    if (shuffledPhotos) {
      const { placed: initialPlaced, unplaced: initialUnplaced } = placePhotos(shuffledPhotos, initialCols, initialRows)
      const { placed: additionalPlaced, unplaced: additionalUnplaced } = placePhotos(initialUnplaced, initialCols)

      setInitialGrid(initialPlaced)
      setRemainingGrid([
        ...additionalPlaced,
        ...additionalUnplaced.map((photo) => ({
          ...photo,
          size: "single",
        })),
      ])
    }
  }, [shuffledPhotos, placePhotos, initialCols, initialRows, initialCount])

  const handleLoadMore = () => {
    setShowAll(true)
  }

  const renderGrid = useCallback(() => {
    const renderedphotos = showAll ? [...initialGrid, ...remainingGrid] : [...initialGrid]

    return renderedphotos.length ? (
      renderedphotos.map((photo, index) => {
        return photo ? (
          <SinglePhoto
            key={photo.uuid}
            photo={photo}
            fallbackImage={fallbackImage}
            setModal={setModal}
            setClickedUUID={setClickedUUID}
          />
        ) : (
          <div key={`empty-photo-${index}`} className="col-span-1 row-span-1" />
        )
      })
    ) : (
      <div className="text-center text-white col-span-full">Nobody matched your filter criteria</div>
    )
  }, [initialGrid, remainingGrid, showAll, fallbackImage])

  return (
    <div>
      {loading ? (
        <div className="px-8 pb-8 mx-auto text-center">
          <Loading className="size-24 mx-auto mb-4 pulse" />
        </div>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-12 gap-4 team-grid">{renderGrid()}</div>
      )}
      {!showAll && !loading && remainingGrid.length > 0 && (
        <div className="flex justify-center mt-6">
          <button
            onClick={handleLoadMore}
            className="px-6 py-3 border-2 cursor-pointer border-white hover:border-blue-bright text-white rounded-4xl uppercase font-secondary"
          >
            Load More
          </button>
        </div>
      )}
      {modalPhoto?.src && (
        <PhotoModal
          isOpen={modal}
          closeModal={() => {
            setModalPhoto({})
            setClickedUUID(null)
            setModal(false)
          }}
        >
          <svg
            className="w-12 h-12 animate-spin text-gray-500 absolute top-1/2 left-1/2 -ml-6 -mt-6 z-10"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="white"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          {modalPhoto?.tooltipContent ? (
            <div className="rounded-4xl">
              <Image
                src={modalPhoto?.src}
                alt={modalPhoto?.alt}
                height={800}
                width={800}
                className="mx-auto rounded-t-4xl bg-blue-to-dark-blue relative z-20"
                style={{ objectFit: "cover" }}
              />
              <div className="bg-kelly-to-lime py-4 px-4 text-blue rounded-b-4xl rounded-none">
                {modalPhoto?.tooltipContent}
              </div>
            </div>
          ) : (
            <Image
              src={modalPhoto?.src}
              alt={modalPhoto?.alt}
              blurDataURL={modalPhoto.blurDataURL}
              height={800}
              width={800}
              className="rounded-4xl mx-auto relative z-20"
              style={{ objectFit: "cover" }}
            />
          )}
        </PhotoModal>
      )}
    </div>
  )
}

export default PhotoGrid
